import { useEffect, useState } from "react";
import { usePage } from "@inertiajs/react";
import { useDeployService } from "@api/deploys.hooks";
import { useDeployType } from "@hooks/useDeployInstructions";

/**
 * Check whether content has changed since the last deploy.
 * Returns `true` when at least one instruction type has changes,
 * `false` when nothing changed, and `null` while loading.
 *
 * Resource-scoped: project-level deploy hits the project endpoint;
 * campaign-level deploy uses the campaign UUID from props.campaign.
 */
export function useHasDeployChanges(): boolean | null {
  const service = useDeployService();
  const props = usePage<{
    project?: { uuid: string };
    campaign?: { uuid: string };
  }>().props;
  const deployType = useDeployType();
  const [hasChanges, setHasChanges] = useState<boolean | null>(null);

  useEffect(() => {
    const projectUuid = props.project?.uuid;
    const campaignUuid = props.campaign?.uuid;

    let cancelled = false;
    const promise =
      deployType === "campaign" && campaignUuid
        ? service.checkCampaignChanges(campaignUuid)
        : projectUuid
          ? service.checkProjectChanges(projectUuid)
          : null;

    if (!promise) return;

    promise
      .then((changes) => {
        if (!cancelled) {
          setHasChanges(Object.values(changes).some((v) => v === true));
        }
      })
      .catch(() => {
        // On error, allow redeploy (fail open)
        if (!cancelled) setHasChanges(true);
      });

    return () => {
      cancelled = true;
    };
  }, [service, props.project?.uuid, props.campaign?.uuid, deployType]);

  return hasChanges;
}
